import { LegacyPortalDashboardService } from "@spwn-portal/grpc";
/* eslint-disable no-restricted-imports -- firebase compat: 新たにコードを追加する時は modular API を使う。modular API へ移行したらこのコメントごと削除する */
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
/* eslint-enable no-restricted-imports */
import useSWR from "swr";

import { useConnectClient } from "../../utility/connectWeb";

import type { ConnectError } from "@bufbuild/connect";
import type {
  LegacyPortalDashboardAgeGroup,
  LegacyPortalDashboardGender,
  LegacyPortalDashboardPrefecture,
} from "@spwn-portal/grpc";

type UserAttributeAgeGroupGenderCount = {
  ageGroup: LegacyPortalDashboardAgeGroup;
  gender: LegacyPortalDashboardGender;
  count: number;
};

type UserAttributeGenderCount = {
  gender: LegacyPortalDashboardGender;
  count: number;
};

type UserAttributePrefectureCount = {
  prefecture: LegacyPortalDashboardPrefecture;
  count: number;
};

export type GetUserAttributeCountsResponse = {
  ageGroupGenderCounts: UserAttributeAgeGroupGenderCount[];
  genderCounts: UserAttributeGenderCount[];
  prefectureCounts: UserAttributePrefectureCount[];
  deletedPurchaserCount: number;
};

type GetUserAttributeCountsError = ConnectError | Error | { message: string };

const GetUserAttributeCountsKey = "getUserAttributeCounts";

export const useGetUserAttributeCounts = (
  eventIds: string[],
  fromDate?: bigint,
  toDate?: bigint
) => {
  const currentUserId = firebase.auth().currentUser?.uid ?? null;
  const connectClient = useConnectClient(LegacyPortalDashboardService);
  const uniqueEventIds = Array.from(new Set(eventIds)).sort();
  const uniqueEventIdsKey = JSON.stringify(uniqueEventIds);

  const {
    data: fetchedData,
    isLoading,
    isValidating,
    error,
    mutate,
  } = useSWR<GetUserAttributeCountsResponse, GetUserAttributeCountsError>(
    currentUserId && uniqueEventIds.length > 0
      ? [
          GetUserAttributeCountsKey,
          currentUserId,
          uniqueEventIdsKey,
          fromDate?.toString() ?? "",
          toDate?.toString() ?? "",
        ]
      : null,
    async () => {
      try {
        const response = await connectClient.getUserAttributeCounts({
          eventIds: uniqueEventIds,
          fromDate,
          toDate,
        });

        return {
          ageGroupGenderCounts: response.ageGroupGenderCounts.map(
            ({ ageGroup, gender, count }) => ({
              ageGroup,
              gender,
              count,
            })
          ),
          genderCounts: response.genderCounts.map(({ gender, count }) => ({
            gender,
            count,
          })),
          prefectureCounts: response.prefectureCounts.map(
            ({ prefecture, count }) => ({
              prefecture,
              count,
            })
          ),
          deletedPurchaserCount: response.deletedPurchaserCount,
        };
      } catch (error) {
        console.error("Error fetching user attribute counts:", error);
        throw error;
      }
    }
  );

  return {
    eventIds: uniqueEventIds,
    isLoading,
    isValidating,
    error,
    mutate,
    fetchedData,
  };
};
